import { createFileRoute } from "@tanstack/react-router";
import { SubPage } from "@/components/sub-page";
import corfo from "@/assets/partners/corfo.png.asset.json";
import cartaNotificacion from "@/assets/carta-notificacion.pdf.asset.json";
import resolucion from "@/assets/resolucion.pdf.asset.json";
import cumplimientoLey from "@/assets/Cumplimiento_Ley_21722.pdf.asset.json";
import balance2025 from "@/assets/MineClass_Balance_2025.pdf.asset.json";
import memoria2025 from "@/assets/MineClass_Memoria_2025.pdf.asset.json";
import eerr2025 from "@/assets/MineClass_EERR_2025.pdf.asset.json";
import recursosLey from "@/assets/Recursos_recibidos_Ley_21_722.pdf.asset.json";
import { FileText, Download } from "lucide-react";

export const Route = createFileRoute("/quienes-somos/corfo-crea-valida")({
  head: () => ({
    meta: [
      { title: "Proyecto Corfo Crea y Valida | Mine Class" },
      { name: "description", content: "Mine Class se adjudica el proyecto Corfo Crea y Valida de InnovaChile para desarrollar un motor de IA de validación documental para la industria minera." },
      { property: "og:title", content: "Proyecto Corfo Crea y Valida | Mine Class" },
      { property: "og:description", content: "Mine Class fue adjudicataria de financiamiento Corfo para desarrollar y validar un motor de IA aplicado a la acreditación laboral en minería." },
      { property: "og:type", content: "article" },
      { property: "og:image", content: `https://mine-class.com${corfo.url}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `https://mine-class.com${corfo.url}` },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SubPage
      section="quienes-somos"
      eyebrow="Proyecto Corfo — Crea y Valida"
      title='Mine Class se adjudica proyecto Corfo "Crea y Valida"'
      intro="Financiamiento público de InnovaChile para acelerar el desarrollo de un motor de IA de validación documental para la industria."
    >
      <div className="space-y-6 text-[var(--color-on-surface-variant)] text-lg leading-relaxed">
        <div className="flex justify-center py-4">
          <img src={corfo.url} alt="Corfo" className="h-24 w-auto object-contain" loading="lazy" />
        </div>

        <p>
          En Mine Class, estamos muy orgullosos de anunciar que hemos sido adjudicatarios de financiamiento
          público a través de <strong>Corfo</strong>, en el marco del programa <strong>Crea y Valida</strong> de InnovaChile.
        </p>
        <p>
          Este importante hito nos permitirá acelerar el desarrollo y validación de una solución tecnológica innovadora
          orientada a transformar procesos críticos en la industria.
        </p>
        <p>
          El proyecto adjudicado tiene como foco el desarrollo de un <strong>motor inteligente de validación documental
          basado en inteligencia artificial</strong>, orientado a optimizar procesos de acreditación laboral en
          industrias de alta exigencia como la minería.
        </p>

        <h2 className="text-2xl font-bold text-[var(--color-on-surface)] pt-4">Nuestra solución busca</h2>
        <ul className="list-disc pl-6 space-y-2">
          <li>Automatizar la revisión de documentación crítica (exámenes, contratos, licencias, entre otros).</li>
          <li>Reducir significativamente los tiempos de acreditación.</li>
          <li>Disminuir errores y riesgos operacionales.</li>
          <li>Aumentar la trazabilidad y estandarización de los procesos.</li>
        </ul>

        <h2 className="text-2xl font-bold text-[var(--color-on-surface)] pt-4">Etapas estratégicas</h2>
        <p>Gracias a este apoyo, trabajaremos en dos líneas estratégicas:</p>

        <h3 className="text-xl font-bold text-[var(--color-on-surface)]">1. Desarrollo y validación tecnológica</h3>
        <p>
          Perfeccionaremos nuestro motor de IA, incorporando capacidades avanzadas de análisis, clasificación y
          validación automática de documentos, con foco en lograr altos niveles de precisión y confiabilidad en
          entornos reales.
        </p>

        <h3 className="text-xl font-bold text-[var(--color-on-surface)]">2. Validación en entorno productivo y escalamiento</h3>
        <p>
          Implementaremos pilotos en la industria para validar el impacto de la solución en condiciones reales,
          preparando su escalamiento comercial en el mercado nacional e internacional.
        </p>

        <p>
          Este proyecto representa un paso clave en nuestra visión de llevar inteligencia artificial aplicada a
          procesos críticos de la industria, generando eficiencia, seguridad y valor para nuestros clientes.
        </p>
        <p>
          Agradecemos el respaldo del Estado de Chile y de Corfo, quienes impulsan la innovación y el desarrollo
          tecnológico del país.
        </p>
        <p className="font-semibold text-[var(--color-on-surface)]">
          Seguimos avanzando en la transformación digital de la industria.
        </p>

        <div className="mt-8 rounded-xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-variant)]/40 p-6">
          <p className="text-base font-medium text-[var(--color-on-surface)] mb-4">
            Descarga la carta de notificación de adjudicación y resolución electrónica exenta del convenio subsidio:
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <a
              href={cartaNotificacion.url}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="flex items-center gap-3 rounded-lg border border-[var(--color-outline-variant)] bg-[var(--color-surface)] p-4 hover:bg-[var(--color-surface-variant)] transition-colors group"
            >
              <FileText className="h-8 w-8 text-[var(--color-primary)] flex-shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[var(--color-on-surface)] text-sm">Carta de notificación</div>
                <div className="text-xs text-[var(--color-on-surface-variant)]">PDF · Adjudicación</div>
              </div>
              <Download className="h-5 w-5 text-[var(--color-on-surface-variant)] group-hover:text-[var(--color-primary)] flex-shrink-0" aria-hidden="true" />
            </a>
            <a
              href={resolucion.url}
              target="_blank"
              rel="noopener noreferrer"
              download
              className="flex items-center gap-3 rounded-lg border border-[var(--color-outline-variant)] bg-[var(--color-surface)] p-4 hover:bg-[var(--color-surface-variant)] transition-colors group"
            >
              <FileText className="h-8 w-8 text-[var(--color-primary)] flex-shrink-0" aria-hidden="true" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-[var(--color-on-surface)] text-sm">Resolución electrónica exenta</div>
                <div className="text-xs text-[var(--color-on-surface-variant)]">PDF · Convenio subsidio</div>
              </div>
              <Download className="h-5 w-5 text-[var(--color-on-surface-variant)] group-hover:text-[var(--color-primary)] flex-shrink-0" aria-hidden="true" />
            </a>
          </div>
        </div>

        <div className="mt-6 rounded-xl border border-[var(--color-outline-variant)] bg-[var(--color-surface-variant)]/40 p-6">
          <h2 className="text-xl font-bold text-[var(--color-on-surface)] mb-4">Documentos Ley 21.722</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {[
              { url: cumplimientoLey.url, label: "Cumplimiento Ley 21.722" },
              { url: balance2025.url, label: "MineClass — Balance 2025" },
              { url: memoria2025.url, label: "MineClass — Memoria 2025" },
              { url: eerr2025.url, label: "MineClass — EERR 2025" },
              { url: recursosLey.url, label: "Recursos recibidos Ley 21.722" },
            ].map((doc) => (
              <a
                key={doc.url}
                href={doc.url}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="flex items-center gap-3 rounded-lg border border-[var(--color-outline-variant)] bg-[var(--color-surface)] p-4 hover:bg-[var(--color-surface-variant)] transition-colors group"
              >
                <FileText className="h-8 w-8 text-[var(--color-primary)] flex-shrink-0" aria-hidden="true" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[var(--color-on-surface)] text-sm">{doc.label}</div>
                  <div className="text-xs text-[var(--color-on-surface-variant)]">PDF · Descargar</div>
                </div>
                <Download className="h-5 w-5 text-[var(--color-on-surface-variant)] group-hover:text-[var(--color-primary)] flex-shrink-0" aria-hidden="true" />
              </a>
            ))}
          </div>
        </div>
      </div>
    </SubPage>
  );
}
